import * as React from "react";
import { Check, Loader2, Save, X } from "lucide-react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Button } from "../../primitives/button";
import { TypedConfirmGate } from "./TypedConfirmGate";
import type { ActionDrawerStatus } from "./types";

export interface ActionDrawerFooterProps {
  status: ActionDrawerStatus;
  /** Disable confirm when validation errors present. */
  canConfirm: boolean;
  /** Show "Modificato" hint when user has edited any field. */
  isDirty: boolean;
  onReject: () => void;
  onSaveDraft?: () => void;
  /**
   * When status is `proposed`/`editing`, fires Conferma (→ `confirming`).
   * When status is `confirming`, fires the final Conferma esecuzione (→ `executing`).
   */
  onConfirm: () => void;
  /** When status is `confirming`, cancel back to `editing`. */
  onCancelConfirm?: () => void;
  /**
   * When set, the final "Esegui ora" step (status `confirming`) is gated
   * behind a type-to-confirm input — the operator must type this phrase
   * verbatim before execute unlocks. Used for irreversible actions
   * (delete record/event/item, destructive db_write, SEPA payment).
   */
  confirmPhrase?: string;
}

/**
 * Footer zone — Rifiuta · Salva bozza · Conferma.
 *
 * The Conferma button is the primary CTA (orange accent). During
 * `confirming` state we show a brief inline verify state ("Confermi?"
 * + Annulla + Esegui ora) before transitioning to `executing`. This matches
 * the v1 "two-tap" pattern that prevented accidental sends.
 */
export const ActionDrawerFooter: React.FC<ActionDrawerFooterProps> = ({
  status,
  canConfirm,
  isDirty,
  onReject,
  onSaveDraft,
  onConfirm,
  onCancelConfirm,
  confirmPhrase,
}) => {
  const { i18n } = useLingui();
  const executing = status === "executing";
  const executed = status === "executed";
  const erroring = status === "error";

  // Type-to-confirm gate — only active on the final "Esegui ora" step.
  const [gatePassed, setGatePassed] = React.useState(false);
  const gateActive = status === "confirming" && Boolean(confirmPhrase);
  React.useEffect(() => {
    // Reset the gate whenever we leave the confirming step so a re-entry
    // requires typing the phrase again.
    if (status !== "confirming") setGatePassed(false);
  }, [status]);
  const blockedByGate = gateActive && !gatePassed;

  if (executed) {
    return (
      <div className="flex items-center gap-2 text-sm text-success-foreground">
        <Check className="h-4 w-4" />
        <span>{i18n._(msg`Azione eseguita.`)}</span>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-2.5">
      {gateActive && confirmPhrase ? (
        <TypedConfirmGate
          phrase={confirmPhrase}
          onValidChange={setGatePassed}
          disabled={executing}
        />
      ) : null}
      <div className="flex w-full flex-wrap items-center justify-between gap-2">
        <div className="text-ink-3 text-xs">
          {isDirty ? i18n._(msg`Modifiche locali in attesa di conferma.`) : null}
        </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onReject}
          disabled={executing}
        >
          <X className="h-4 w-4" />
          {i18n._(msg`Rifiuta`)}
        </Button>
        {onSaveDraft ? (
          <Button
            variant="outline"
            size="sm"
            onClick={onSaveDraft}
            disabled={executing}
          >
            <Save className="h-4 w-4" />
            {i18n._(msg`Salva bozza`)}
          </Button>
        ) : null}
        {status === "confirming" && onCancelConfirm ? (
          <Button variant="outline" size="sm" onClick={onCancelConfirm}>
            {i18n._(msg`Annulla`)}
          </Button>
        ) : null}
        <Button
          variant="default"
          size="sm"
          onClick={onConfirm}
          disabled={!canConfirm || executing || blockedByGate}
          aria-busy={executing || undefined}
        >
          {executing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Check className="h-4 w-4" />
          )}
          {status === "confirming"
            ? i18n._(msg`Esegui ora`)
            : erroring
              ? i18n._(msg`Riprova`)
              : i18n._(msg`Conferma`)}
        </Button>
        </div>
      </div>
    </div>
  );
};
ActionDrawerFooter.displayName = "ActionDrawerFooter";
